import { Component } from "react"

/* Components */
import { Cards, Notes, Transaction, InnerLayout } from "../../components"

/* Widgets */
import RoundStat from "../Round/RoundStat"
import RoundPayment from "../Round/RoundPayment"
import RoundRefunds from "../Round/RoundRefunds"

/* REST API */
import * as api from "../../api/VipPoker"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* Helpers */
import { utils } from "../../helpers"

/* Toast */
import { pushToast } from "../../components/Toast"
import { pushConfirm } from "../../components/Confirm"

/* Icons */
import {
    SpadeIcon, HashIcon, TagIcon, ClockIcon, DealerIcon, FlagIcon, PauseIcon, EditIcon,
    PlayerIcon, BalanceIcon, DebitIcon, CreditIcon, NetIcon, RefundIcon, PayIcon,
    StopIcon, PlayIcon, ReceiptIcon, CardsIcon, ArrowIcon, BetsIcon, ArrowRightIcon, WarningIcon, EyeOffIcon,
} from "../../components/Icons"


/*
    Widget GameInformation — THE ROUND OF A VIP TABLE

    One player sits at a vip round, so the card carries no player list: the money of that single
    player is the money of the round. Everything else is the concept every other game shares.
*/
class GameInformation extends Component {

    constructor() {
        super()

        this.state = {
            table: null,
            data: null,
            transactions: [],
            gameProcess: [],
            results: [],
            cards: [],
            status: "loading",
        }
    }

    componentDidMount = () => {
        this.load()
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { uid, gameID, postfix } = this.props

        /* Check Parameters */
        if (!uid || !gameID || !postfix) {
            this.setState({ status: "error", data: null })
            return
        }

        /* Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        api.game({ uid, gameID, postfix }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.game,
                    table: response.data.table,
                    transactions: response.data.transactions ? response.data.transactions : [],
                    gameProcess: response.data.gameProcess ? response.data.gameProcess : [],
                    cards: response.data.cards ? response.data.cards : [],
                    results: response.data.results ? response.data.results : [],
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: null, table: null })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: null, table: null })
            }
            else {
                this.setState({ status: "error", data: null, table: null })
            }
        }).catch(() => {
            this.setState({ status: "network", data: null, table: null })
        })
    }

    /* Get The Result Of One Row Of Cards */
    getResult = (type, box) => {

        /* Fields */
        const { results } = this.state

        /* Check Results */
        if (!results || !Array.isArray(results) || results.length === 0) {
            return null
        }

        /* Fields */
        const found = results.find((item) => item.type === type && parseInt(item.box) === parseInt(box))

        return found ? found : null
    }

    /* Get The Cards Of One Row */
    getCards = (type, box) => {

        /* Fields */
        const { cards } = this.state
        const list = []

        /* Check Cards */
        if (cards && Array.isArray(cards) && cards.length > 0) {
            cards.forEach((card) => {
                if (card.type === type && parseInt(card.box) === parseInt(box)) {
                    list.push(card)
                }
            })
        }

        return list
    }

    /* THE MONEY OF THE ROUND — ONLY THE TRANSACTIONS THE OPERATOR CONFIRMED COUNT */
    getTotals = () => {

        /* Fields */
        const { data, transactions } = this.state

        /* Fields */
        const start = data && data.startBalance ? parseFloat(data.startBalance) : 0
        let debit = 0
        let credit = 0

        /* Check Transactions */
        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            transactions.forEach((transaction) => {

                /* Check Status */
                if (parseInt(transaction.status) !== 1) {
                    return
                }

                if (transaction.type === "credit") {
                    credit = credit + parseFloat(transaction.total)
                }

                if (transaction.type === "debit") {
                    debit = debit + parseFloat(transaction.total)
                }

            })
        }

        return {
            debit,
            credit,
            total: debit - credit,
            start,

            /* The wallet only adds up once the round is closed */
            end: parseInt(data.status) === 1 ? start - debit + credit : null,
        }
    }

    /* THE FAILED AND THE HANGING TRANSACTIONS OF THE ROUND */
    getProblem = () => {

        /* Fields */
        const { data } = this.state

        /* Fields */
        const failed = parseInt(data.failed) > 0 ? parseInt(data.failed) : 0
        const pending = parseInt(data.pending) > 0 ? parseInt(data.pending) : 0

        /* Check Counters */
        if (failed === 0 && pending === 0) {
            return null
        }

        /* A Failure Outweighs A Hanging Request */
        return {
            tone: failed > 0 ? "failed" : "pending",
            count: failed + pending,
            hint: [
                failed > 0 ? `${t("gtx.failed")}: ${failed}` : "",
                pending > 0 ? `${t("gtx.pending")}: ${pending}` : "",
            ].filter((part) => part).join(" · "),
        }
    }

    /* Open The Refunds Of The Round */
    openRefunds = () => {

        /* Fields */
        const { uid } = this.props
        const { data } = this.state

        /* Check Modal */
        if (!this._refunds || !data) {
            return
        }

        this._refunds.open({ uid, round: data.roundId })
    }

    /* Open The Payment Modal */
    payment = (type) => {

        /* Fields */
        const { data, gameProcess } = this.state

        /* Check Modal */
        if (!this._payment || !data) {
            return
        }

        /* Fields */
        const ante = gameProcess.find((item) => item.type === "ante")
        const totals = this.getTotals()

        this._payment.open({ type, game: { ...data, ante: ante ? ante.total : 0, total: totals.debit } })
    }


    /* END ACTION — THE ROUND IS CLOSED BY HAND, AND THE BETS GO BACK WHEN THEY ARE ASKED TO */
    end = (refund = false) => {

        /* Fields */
        const { uid, postfix, socket } = this.props
        const { data } = this.state

        /* Check Status */
        if (parseInt(data.status) === 1) {
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.finishConfirm"),
            text: t("widget.finishWarning"),
            icon: "warning",
            danger: true,
            confirmText: t("widget.finishButton"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            api.endGame({ uid, gameID: data.id, postfix }).then((response) => {
                if (response.status !== 200) {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                    return
                }

                /* Notify The Player — only while this round is still the live one, an old
                   round must never end the game that is running now */
                if (socket && response.data.update === "yes") {
                    socket.emit("end", { gameID: data.id })
                }

                /* The Round Is Only Closed */
                if (!refund) {

                    if (response.data.update === "yes") {
                        pushToast({ text: t("widget.finishSuccess"), type: "success" })
                    }
                    else {
                        pushToast({ text: t("widget.warningStatusDb"), type: "warning" })
                    }

                    this.load(true)
                    return
                }

                /* The Bets Go Back — whatever was not given back already */
                const totals = this.getTotals()
                const given = data.refund ? parseFloat(data.refund) : 0
                const total = totals.debit - given

                /* Check Total */
                if (!(total > 0)) {
                    this.load()
                    pushToast({ text: t("widget.finishTxError"), type: "error" })
                    return
                }

                /* Send to REST API — the postfix names the tables of the game, the call is nothing without it */
                api.credit({ uid, type: "refund", refund: total, currency: data.currency, playerID: data.player, id: data.id, postfix }).then((answer) => {
                    if (answer.status === 200) {

                        if (response.data.update === "yes") {

                            if (socket) {
                                socket.emit("adminNotification", { type: "refund", title: t("widget.refundTitle2"), total, currency: data.currency, playerID: data.player })
                            }

                            pushToast({ text: t("widget.finishSuccess"), type: "success" })
                            this.load(true)
                        }
                        else {
                            this.load()
                            pushToast({ text: t("widget.warningStatusDb"), type: "warning" })
                        }
                    }
                    else {
                        this.load()
                        pushToast({ text: t("widget.finishTxError"), type: "error" })
                    }
                }).catch(() => {
                    this.load()
                    pushToast({ text: t("widget.finishTxError"), type: "error" })
                })

            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            })

        })

    }

    /* PAUSE ACTION */
    pause = () => {

        /* Fields */
        const { uid, postfix, socket } = this.props
        const { data } = this.state

        /* Check Status */
        if (parseInt(data.status) === 1) {
            pushToast({ text: t("widget.gameFinished"), type: "error" })
            return
        }

        /* Check Paused */
        if (parseInt(data.isPaused) === 1) {
            pushToast({ text: t("widget.alreadyPaused"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.pauseConfirm"),
            text: t("widget.pauseWarning"),
            icon: "warning",
            confirmText: t("widget.pauseButton"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            api.togglePause({ uid, gameID: data.id, type: "pause", postfix }).then((response) => {
                if (response.status === 200) {

                    if (response.data.update === "yes") {

                        if (socket) {
                            socket.emit("pause", { from: "support" })
                        }

                        pushToast({ text: t("widget.gamePausedSuccess"), type: "success" })
                    }
                    else {
                        pushToast({ text: t("widget.warningStatusDb"), type: "warning" })
                    }

                    this.load(true)
                }
                else {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            })

        })

    }

    /* CONTINUE ACTION */
    continue = () => {

        /* Fields */
        const { uid, postfix, socket } = this.props
        const { data } = this.state

        /* Check Status */
        if (parseInt(data.status) === 1) {
            pushToast({ text: t("widget.gameFinished"), type: "error" })
            return
        }

        /* Check Paused */
        if (parseInt(data.isPaused) === 0) {
            pushToast({ text: t("widget.gameNotPaused"), type: "error" })
            return
        }

        /* Ask */
        pushConfirm({
            title: t("widget.continueConfirm"),
            text: t("widget.continueText"),
            icon: "warning",
            confirmText: t("widget.continueWithout"),
            cancelText: t("swal.cancelButton"),
        }).then((confirmed) => {

            /* Check Answer */
            if (!confirmed) {
                return
            }

            /* Send to REST API */
            api.togglePause({ uid, gameID: data.id, type: "continue", postfix }).then((response) => {
                if (response.status === 200) {

                    if (response.data.update === "yes") {

                        if (socket) {
                            socket.emit("continue", { type: "continue", gameID: data.id })
                        }

                        pushToast({ text: t("widget.gameContinued"), type: "success" })
                    }
                    else {
                        pushToast({ text: t("widget.warningStatusDb"), type: "warning" })
                    }

                    this.load(true)
                }
                else {
                    pushToast({ text: t("widget.somethingWrong"), type: "error" })
                }
            }).catch(() => {
                pushToast({ text: t("widget.somethingWrong"), type: "error" })
            })

        })

    }


    /* Draw The Head Of The Round */
    _hero = () => {

        /* Fields */
        const { data, table } = this.state

        /* Fields */
        const datetime = utils.getTime(data.createdAt)
        const totals = this.getTotals()
        const problem = this.getProblem()

        return (
            <div className="rnd--hero">

                <div className="rnd--hero-main">

                    <div className="rnd--hero-round">#{data.number}</div>

                    <div className="rnd--hero-text">
                        <h3>{table.name}</h3>
                        <p><ClockIcon size={11} />{datetime.time}, {datetime.date}</p>
                    </div>

                </div>

                <div className="rnd--hero-badges">

                    {parseInt(data.status) === 1
                        ? <div className="rnd--badge blue">{t("widget.completed")}</div>
                        : <div className="rnd--badge violet">{t("widget.notCompleted")}</div>
                    }

                    {parseInt(data.isPaused) === 1 && <div className="rnd--badge red"><PauseIcon size={11} />{t("widget.paused")}</div>}

                    {parseInt(data.isUpdated) === 1 && <div className="rnd--badge pink"><EditIcon size={11} />{t("widget.changed")}</div>}

                    {/* The money of the round never went through */}
                    {problem &&
                        <div className={`rnd--badge ${problem.tone}`} title={problem.hint}>
                            <WarningIcon size={11} />
                            {t("round.problem")} {problem.count}
                        </div>
                    }

                </div>

                <div className="rnd--hero-money">

                    <div className="rnd--hero-money-row">
                        <span>CREDIT</span>
                        <p>{utils.convertor(totals.credit, data.symbol)}</p>
                    </div>

                    <div className="rnd--hero-money-row">
                        <span>DEBIT</span>
                        <p>{utils.convertor(totals.debit, data.symbol)}</p>
                    </div>

                    <div className={`rnd--hero-net ${totals.total > 0 ? "green" : (totals.total < 0 ? "red" : "")}`}>
                        <span>{t("widget.total")}</span>
                        <p>
                            {totals.total > 0 ? "+ " : (totals.total < 0 ? "- " : "")}
                            {utils.convertor(Math.abs(totals.total), data.symbol)}
                        </p>
                    </div>

                </div>

            </div>
        )
    }

    /* Draw The Bets Of The Player */
    _bets = () => {

        /* Fields */
        const { data, gameProcess } = this.state

        /* Check Bets */
        if (!gameProcess || !Array.isArray(gameProcess) || gameProcess.length === 0) {
            return null
        }

        return (
            <div className="rnd--bets">

                <div className="rnd--bets-head">
                    <BetsIcon size={13} />
                    {t("round.bets")}
                    <span className="rnd--bets-count">{gameProcess.length}</span>
                </div>

                <div className="rnd--bets-list">
                    {gameProcess.map((item, index) =>
                        <div className="rnd--bet" key={`${index}`}>

                            <div className={`rnd--bet-name ${item.type}`}>
                                {`${item.type}`.toUpperCase()}
                                {item.box ? <b>BOX {item.box}</b> : null}
                            </div>

                            <div className="rnd--bet-total">{utils.convertor(item.total, data.symbol)}</div>

                            <div className={`rnd--bet-win ${parseFloat(item.win) > 0 ? "" : "lose"}`}>
                                {parseFloat(item.win) > 0 ? `+ ${utils.convertor(item.win, data.symbol)}` : t("widget.noWin")}
                            </div>

                        </div>
                    )}
                </div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { uid, postfix, socket } = this.props
        const { status, data, table, transactions } = this.state

        /* Layout */
        if (status !== "ok" || !data || !table) {
            return <InnerLayout status={status === "ok" ? "error" : status} reload={() => this.load(true)} />
        }

        /* Fields */
        const datetime = utils.getTime(data.createdAt)
        const totals = this.getTotals()
        const done = parseInt(data.status) === 1
        const paused = parseInt(data.isPaused) === 1

        return (
            <div className="rnd">

                {/* Draw Hero */}
                {this._hero()}

                <div className="rnd--grid">

                    {/* WHAT THE ROUND IS */}
                    <div className="rnd--card">

                        <div className="rnd--card-head"><SpadeIcon size={13} />{t("round.about")}</div>

                        <div className="rnd--rows">

                            <RoundStat icon={<SpadeIcon size={14} />} title={t("widget.game")}>{table.name}</RoundStat>

                            <RoundStat icon={<HashIcon size={14} />} title={t("widget.gameNumber2")}>{data.number}</RoundStat>

                            <RoundStat icon={<TagIcon size={14} />} tone="blue" title={t("widget.roundId")}>
                                <span className="rnd--mono">{utils.checkValue(data.roundId, "—")}</span>
                            </RoundStat>

                            <RoundStat icon={<ClockIcon size={14} />} title={t("widget.date")}>
                                {datetime.time}, <span className="rnd--muted">{datetime.date}</span>
                            </RoundStat>

                            <RoundStat icon={<DealerIcon size={14} />} title={t("widget.dealer")}>{utils.checkValue(data.dealer, "—")}</RoundStat>

                            <RoundStat icon={<PlayerIcon size={14} />} tone="blue" title={`${t("widget.player")} ${data.player}`}>
                                {utils.checkValue(data.firstName, "—")}
                            </RoundStat>

                        </div>

                    </div>

                    {/* WHAT THE ROUND DID TO THE MONEY */}
                    <div className="rnd--card">

                        <div className="rnd--card-head"><ReceiptIcon size={13} />{t("round.money")}</div>

                        <div className="rnd--rows">

                            <RoundStat icon={<DebitIcon size={14} />} tone="green" title="DEBIT">{utils.convertor(totals.debit, data.symbol)}</RoundStat>

                            <RoundStat icon={<CreditIcon size={14} />} tone="red" title="CREDIT">{utils.convertor(totals.credit, data.symbol)}</RoundStat>

                            <RoundStat icon={<NetIcon size={14} />} tone="violet" title={t("widget.total")}>
                                <span className={totals.total >= 0 ? "green" : "red"}>
                                    {totals.total >= 0 ? "+ " : "- "}{utils.convertor(Math.abs(totals.total), data.symbol)}
                                </span>
                            </RoundStat>

                            {/* The wallet of the player around the round */}
                            <RoundStat icon={<BalanceIcon size={14} />} title={t("widget.balanceBefore")}>
                                {utils.convertor(totals.start, data.symbol)}
                            </RoundStat>

                            <RoundStat icon={<ArrowRightIcon size={14} />} tone="blue" title={t("widget.balanceAfter")}>
                                {totals.end === null ? <span className="rnd--empty">—</span> : utils.convertor(totals.end, data.symbol)}
                            </RoundStat>

                            {/* Every operation of the round, the refunds of the admins first.
                                They are keyed by the round id — an old round without one carries none */}
                            {`${utils.checkValue(data.roundId)}`.trim() === ""
                                ? <div className="rnd--link disabled" title={t("round.noRoundId")}>
                                    <RefundIcon size={13} />
                                    <span>{t("widget.allRefunds")}</span>
                                </div>
                                : <div onClick={() => this.openRefunds()} className="rnd--link">
                                    <RefundIcon size={13} />
                                    <span>{t("widget.allRefunds")}</span>
                                    <ArrowIcon size={12} />
                                </div>
                            }

                        </div>

                    </div>

                    {/* WHAT THE ADMIN CAN STILL DO */}
                    <div className="rnd--card">

                        <div className="rnd--card-head"><FlagIcon size={13} />{t("round.actions")}</div>

                        <div className="rnd--groups">

                            <div className="rnd--group">

                                <div className="rnd--group-title">{t("round.gameActions")}</div>

                                <div className="rnd--buttons">
                                    <div onClick={() => this.end()} className={`rnd--button danger ${done ? "disabled" : ""}`}>
                                        <StopIcon size={13} />{t("widget.finishGame")}
                                    </div>
                                    <div onClick={() => this.end(true)} className={`rnd--button ghost ${done ? "disabled" : ""}`}>
                                        <RefundIcon size={13} />{t("widget.finishAndRefund")}
                                    </div>
                                </div>

                            </div>

                            <div className="rnd--group">

                                <div className="rnd--group-title">{t("round.pauseActions")}</div>

                                <div className="rnd--buttons">
                                    <div onClick={() => this.pause()} className={`rnd--button blue ${paused ? "disabled" : ""}`}>
                                        <PauseIcon size={13} />{t("widget.pauseGame")}
                                    </div>
                                    <div onClick={() => this.continue()} className={`rnd--button green ${paused ? "" : "disabled"}`}>
                                        <PlayIcon size={13} />{t("widget.continueWithout")}
                                    </div>
                                </div>

                            </div>

                            <div className="rnd--group">

                                <div className="rnd--group-title">{t("round.paymentActions")}</div>

                                <div className="rnd--buttons">
                                    <div onClick={() => this.payment("refund")} className="rnd--button violet">
                                        <RefundIcon size={13} />{t("widget.refundTitle")}
                                    </div>
                                    <div onClick={() => this.payment("payment")} className="rnd--button pink">
                                        <PayIcon size={13} />{t("widget.payTitle")}
                                    </div>
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

                {/* Draw Cards — the cards of the round come before everything the player did, and the hand
                    of a live round stays closed until the round ends or stands on pause */}
                <div className="rnd--cards">

                    <div className="rnd--card-head"><CardsIcon size={13} />{t("round.cards")}</div>

                    {done || paused
                        ? <div className="cards--container">
                            <Cards title={t("round.dealerCards")} cards={this.getCards("dealer", 0)} result={this.getResult("dealer", 0)} />
                            <Cards title={`${t("round.box")} 1`} cards={this.getCards("player", 1)} result={this.getResult("player", 1)} />
                            <Cards title={`${t("round.box")} 2`} cards={this.getCards("player", 2)} result={this.getResult("player", 2)} />
                        </div>
                        : <div className="rnd--cards-hidden"><EyeOffIcon size={16} />{t("round.cardsHidden")}</div>
                    }

                </div>

                {/* Draw Bets */}
                {this._bets()}

                {/* Draw Transactions */}
                <Transaction symbol={data.symbol} data={transactions} />

                {/* Draw Notes */}
                <Notes uid={table.uid} gameID={data.id} />

                {/* Draw Payment */}
                <RoundPayment
                    ref={(ref) => this._payment = ref}
                    api={api}
                    uid={uid}
                    postfix={postfix}
                    socket={socket}
                    apply={() => this.load()}
                    build={({ type, amount, game }) => ({ uid, type, refund: amount, currency: game.currency, playerID: game.player, id: game.id, postfix })}
                />

                {/* Draw Refunds */}
                <RoundRefunds ref={(ref) => this._refunds = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps)(GameInformation)
